import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import GradeBadge from '../../components/shared/GradeBadge';
import ScoringStatusChip from '../../components/shared/ScoringStatusChip';
import type { Tenant, Video, LeaderboardEntry, Grade, ScoringStatus } from '../../types';

const PLAN_LABELS: Record<string, string> = {
  DEMO: 'Demo',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

function scoreGrade(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

const GRADE_COLORS: Record<string, { text: string; bg: string }> = {
  A: { text: 'text-emerald-500', bg: 'bg-emerald-500' },
  B: { text: 'text-primary', bg: 'bg-primary' },
  C: { text: 'text-amber-500', bg: 'bg-amber-400' },
  D: { text: 'text-orange-500', bg: 'bg-orange-500' },
  F: { text: 'text-red-500', bg: 'bg-red-500' },
};

interface SalesRep {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

const GroupAdminTenantDetailPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [repPage, setRepPage] = useState(1);
  const REP_PAGE_SIZE = 10;

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [metrics, setMetrics] = useState<LeaderboardEntry | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [tenantsRes, metricsRes, videosRes, repsRes] = await Promise.all([
        api.get('/group-admin/tenants'),
        api.get('/group-admin/tenants/metrics'),
        api.get(`/group-admin/videos?tenantId=${tenantId}&pageSize=6&sort=newest`),
        api.get(`/group-admin/tenants/${tenantId}/users`),
      ]);

      const tenantList: Tenant[] = tenantsRes.data.data ?? [];
      const found = tenantList.find((t) => t.id === tenantId) ?? null;
      setTenant(found);

      const metricsList: LeaderboardEntry[] = metricsRes.data.data ?? [];
      const foundMetrics = metricsList.find((m) => m.tenantId === tenantId) ?? null;
      setMetrics(foundMetrics);

      const videoList: Video[] = videosRes.data.data ?? [];
      setVideos(videoList);
      setSalesReps(repsRes.data.data ?? []);

      if (videoList.length > 0) {
        try {
          const thumbRes = await api.post('/group-admin/thumbnail-urls', { videoIds: videoList.map((v) => v.id) });
          setThumbnailUrls(thumbRes.data.data ?? {});
        } catch {
          setThumbnailUrls({});
        }
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const grade = metrics && metrics.totalVideos > 0 ? scoreGrade(metrics.averageScore) : null;
  const gradeColors = grade ? GRADE_COLORS[grade] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col">
        <TopNav />
        <div className="flex flex-col items-center justify-center flex-1 gap-4">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
          <span className="text-sm font-bold text-slate-400 dark:text-slate-500 tracking-tight">Loading dealership...</span>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col">
        <TopNav />
        <div className="flex flex-col items-center justify-center flex-1 gap-4">
          <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">business_off</span>
          <p className="text-slate-500 dark:text-slate-400 font-medium">Dealership not found.</p>
          <button type="button" onClick={() => navigate('/group-admin/tenants')} className="text-primary font-bold text-sm underline underline-offset-2">
            Back to Dealerships
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">

        {/* Back + Header */}
        <div className="mb-10">
          <button
            type="button"
            onClick={() => navigate('/group-admin/tenants')}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-primary transition-colors mb-4"
          >
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to Dealerships
          </button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
                {tenant.name}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                  {PLAN_LABELS[tenant.plan] ?? tenant.plan}
                </span>
                <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase ${tenant.isActive ? 'text-primary' : 'text-slate-400'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${tenant.isActive ? 'bg-primary' : 'bg-slate-300'}`} />
                  {tenant.isActive ? 'Active' : 'Inactive'}
                </div>
                <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">{tenant.slug}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/group-admin/videos?tenantId=${tenant.id}`)}
              className="h-10 px-6 rounded-xl font-bold bg-primary/10 dark:bg-primary/20 text-primary hover:bg-primary/20 transition-all text-xs uppercase tracking-widest flex items-center gap-2 shrink-0"
            >
              <span className="material-symbols-outlined text-[16px]">play_circle</span>
              View All Videos
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            {
              label: 'Total Videos',
              value: metrics ? metrics.totalVideos.toLocaleString() : '—',
              icon: 'play_circle',
              accent: 'text-violet-600 dark:text-violet-400',
            },
            {
              label: 'Avg VQS Score',
              value: metrics && metrics.totalVideos > 0 ? metrics.averageScore.toFixed(1) : '—',
              icon: 'leaderboard',
              accent: gradeColors ? gradeColors.text : 'text-slate-400',
              sub: grade ? `Grade ${grade}` : undefined,
            },
            {
              label: 'Flag Rate',
              value: metrics && metrics.totalVideos > 0 ? `${(metrics.complianceFlagRate * 100).toFixed(1)}%` : '—',
              icon: 'flag',
              accent: metrics && metrics.complianceFlagRate > 0.25 ? 'text-red-500' : metrics && metrics.complianceFlagRate > 0.1 ? 'text-amber-500' : 'text-emerald-500',
            },
            {
              label: 'Sales Reps',
              value: String(salesReps.length),
              icon: 'groups',
              accent: 'text-primary',
            },
          ].map((card) => (
            <div key={card.label} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">{card.label}</p>
                <span className={`material-symbols-outlined text-[20px] ${card.accent} opacity-70`}>{card.icon}</span>
              </div>
              <p className={`text-3xl font-black tracking-tight ${card.accent}`}>{card.value}</p>
              {card.sub && <p className="text-[10px] font-semibold text-slate-400 -mt-2">{card.sub}</p>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Videos */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Recent Videos</h2>
              <button
                type="button"
                onClick={() => navigate(`/group-admin/videos?tenantId=${tenant.id}`)}
                className="text-xs font-bold text-primary hover:underline underline-offset-2"
              >
                View all
              </button>
            </div>
            {videos.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl flex flex-col items-center justify-center py-16 gap-3 text-slate-400 dark:text-slate-600">
                <span className="material-symbols-outlined text-[40px]">videocam_off</span>
                <p className="text-sm font-medium">No videos yet for this dealership.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {videos.map((video) => {
                  const isClickable = video.scoringStatus !== 'PENDING' && video.scoringStatus !== 'PROCESSING';
                  const thumbUrl = thumbnailUrls[video.id];
                  return (
                    <div
                      key={video.id}
                      onClick={() => isClickable && navigate(`/videos/${video.id}`)}
                      className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex gap-0 transition-all group ${isClickable ? 'cursor-pointer hover:border-primary/30 hover:shadow-md hover:shadow-primary/5' : 'opacity-60'}`}
                    >
                      <div className="w-28 h-18 shrink-0 bg-slate-100 dark:bg-slate-800 relative overflow-hidden" style={{ height: '72px' }}>
                        {thumbUrl ? (
                          <img src={thumbUrl} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="material-symbols-outlined text-[24px] text-slate-300 dark:text-slate-600">videocam</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 px-4 py-3 flex items-center justify-between gap-4 min-w-0">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-primary transition-colors">{video.title}</p>
                          <p className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest mt-0.5">
                            {new Date(video.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="shrink-0">
                          {video.videoScore ? (
                            <div className="flex items-center gap-2">
                              <GradeBadge grade={video.videoScore.grade as Grade} size="sm" />
                              <span className="text-sm font-black text-slate-900 dark:text-white">{video.videoScore.compositeScore}</span>
                            </div>
                          ) : (
                            <ScoringStatusChip status={video.scoringStatus as ScoringStatus} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sales Reps */}
          <div>
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">Sales Reps</h2>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              {salesReps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400 dark:text-slate-600">
                  <span className="material-symbols-outlined text-[32px]">group_off</span>
                  <p className="text-sm font-medium">No sales reps.</p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {salesReps.slice((repPage - 1) * REP_PAGE_SIZE, repPage * REP_PAGE_SIZE).map((rep) => (
                      <div key={rep.id} className="px-5 py-4 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                          {(rep.firstName ?? rep.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                            {[rep.firstName, rep.lastName].filter(Boolean).join(' ') || rep.email}
                          </p>
                          <p className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest truncate">
                            {rep.email}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {salesReps.length > REP_PAGE_SIZE && (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800">
                      <button
                        type="button"
                        disabled={repPage === 1}
                        onClick={() => setRepPage(repPage - 1)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary hover:bg-primary/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        {'<'}
                      </button>
                      <span className="text-xs font-bold text-slate-400 dark:text-slate-500">
                        {repPage} / {Math.ceil(salesReps.length / REP_PAGE_SIZE)}
                      </span>
                      <button
                        type="button"
                        disabled={repPage >= Math.ceil(salesReps.length / REP_PAGE_SIZE)}
                        onClick={() => setRepPage(repPage + 1)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary hover:bg-primary/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        {'>'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GroupAdminTenantDetailPage;
